import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import DatePicker from "~/molecules/DatePicker";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { reviewPeriod } from "~/config/types";
import { addDays } from "~/helpers/date";
import { getReviewPeriodLabel } from "~/helpers/document";
import { Button } from "~/atoms/button";
import { Trash } from "lucide-react";
import { type DocumentEditFormValues } from "~/hooks/useDocumentEditForm";

type ScheduledReviewFormFieldValues = Pick<
  DocumentEditFormValues,
  "scheduleReviewFrom" | "scheduleReviewTo" | "scheduleReviewPeriod"
>;

export const DocumentScheduledReviewFields = () => {
  const { t } = useTranslation();
  const form = useFormContext<ScheduledReviewFormFieldValues>();

  const scheduleReviewFrom = useWatch({
    control: form.control,
    name: "scheduleReviewFrom",
  });

  const handleStartDateChange = (date?: Date) => {
    const dateValue = date ?? null;
    form.setValue("scheduleReviewFrom", dateValue);

    if (date) {
      const currentDueDate = form.getValues("scheduleReviewTo");

      if (currentDueDate && currentDueDate < date) {
        form.setValue("scheduleReviewTo", null);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <FormField
          control={form.control}
          name="scheduleReviewFrom"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>{t("modal.reviewSchedule.startDate")}</FormLabel>
              <FormControl>
                <DatePicker
                  onChange={handleStartDateChange}
                  value={field.value}
                  minDate={new Date()}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="scheduleReviewTo"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>{t("modal.reviewSchedule.dueDate")}</FormLabel>
              <FormControl>
                <DatePicker
                  onChange={(date) => field.onChange(date ?? null)}
                  value={field.value}
                  minDate={
                    scheduleReviewFrom
                      ? addDays(scheduleReviewFrom, 1)
                      : new Date()
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="scheduleReviewPeriod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("modal.reviewSchedule.recurringPeriod")}</FormLabel>
            <FormControl>
              <div className="flex gap-2">
                <ControlledCombobox
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  placeholder={t("commons.customSelect.selectOption")}
                  options={reviewPeriod.map((value) => ({
                    value,
                    label: getReviewPeriodLabel(value, t) ?? value,
                  }))}
                />
                {field.value && (
                  <Button
                    onClick={() => field.onChange(null)}
                    variant="ghost"
                    size="icon"
                    type="button"
                  >
                    <Trash />
                  </Button>
                )}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
